'use client'

import { useState } from 'react'
import Link from 'next/link'
import { Search, BookOpen, Users } from 'lucide-react'
import { Header } from '@/components/layout/header'
import { Footer } from '@/components/layout/footer'

export default function GuidesPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTool, setSelectedTool] = useState('all')

  const guides = [
    {
      id: '1',
      title: 'Getting started with ChatGPT',
      description: 'A complete beginner\'s guide to using ChatGPT for the first time. Learn the basics and start asking better questions.',
      difficulty: 'Beginner',
      tool: 'ChatGPT',
      reads: 234,
      slug: 'getting-started-chatgpt',
    },
    {
      id: '2',
      title: 'Prompting 101: How to get better AI answers',
      description: 'The science behind good prompts. Learn the framework that makes AI actually useful instead of frustrating.',
      difficulty: 'Beginner',
      tool: 'General',
      reads: 512,
      slug: 'prompting-101',
    },
    {
      id: '3',
      title: 'AI tools for small business owners',
      description: 'Cut the noise. Here are the exact AI tools worth your time if you run a small business.',
      difficulty: 'Intermediate',
      tool: 'General',
      reads: 189,
      slug: 'ai-tools-small-business',
    },
    {
      id: '4',
      title: 'Using Claude for research and analysis',
      description: 'Deep research with AI. Learn how to use Claude\'s advanced reasoning for complex analysis.',
      difficulty: 'Intermediate',
      tool: 'Claude',
      reads: 156,
      slug: 'claude-research-analysis',
    },
    {
      id: '5',
      title: 'Image generation with DALL-E and Midjourney',
      description: 'Visual creativity at scale. Compare tools, learn techniques, and avoid common mistakes.',
      difficulty: 'Intermediate',
      tool: 'Image Generation',
      reads: 298,
      slug: 'image-generation-guide',
    },
    {
      id: '6',
      title: 'No-code automation with Zapier',
      description: 'Connect your tools without code. Learn to automate repetitive tasks and save hours each week.',
      difficulty: 'Beginner',
      tool: 'Zapier',
      reads: 401,
      slug: 'zapier-automation-guide',
    },
    {
      id: '7',
      title: 'AI for content creators',
      description: 'Using AI as a creative partner. Practical techniques for writers, videographers, and designers.',
      difficulty: 'Intermediate',
      tool: 'General',
      reads: 267,
      slug: 'ai-content-creators',
    },
    {
      id: '8',
      title: 'Building AI-powered workflows',
      description: 'Advanced: Combine multiple AI tools into powerful workflows that scale your work.',
      difficulty: 'Advanced',
      tool: 'General',
      reads: 143,
      slug: 'ai-workflows',
    },
  ]

  const tools = ['all', 'ChatGPT', 'Claude', 'Zapier', 'Image Generation', 'General']

  const filteredGuides = guides.filter((guide) => {
    const matchesSearch = guide.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guide.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesTool = selectedTool === 'all' || guide.tool === selectedTool
    return matchesSearch && matchesTool
  })

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner':
        return 'bg-success/10 text-success'
      case 'Intermediate':
        return 'bg-warning/10 text-warning'
      case 'Advanced':
        return 'bg-error/10 text-error'
      default:
        return 'bg-gray-100 text-charcoal'
    }
  }

  return (
    <div className="min-h-screen bg-background text-charcoal">
      <Header />

      {/* Header Section */}
      <section className="bg-white border-b border-gray-200 py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-5 h-5 text-primary" />
              <span className="text-sm font-semibold text-primary">LEARNING CENTER</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">Guides</h1>
            <p className="text-lg text-warm-gray max-w-2xl">
              Clear, practical guides to help you master AI tools. Written for normal people.
            </p>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="w-5 h-5 absolute left-4 top-3.5 text-warm-gray" />
            <input
              type="text"
              placeholder="Search guides..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-300 focus:border-primary focus:outline-none transition-colors"
            />
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-background border-b border-gray-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="overflow-x-auto">
            <div className="flex gap-2 pb-2">
              {tools.map((tool) => (
                <button
                  key={tool}
                  onClick={() => setSelectedTool(tool)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                    selectedTool === tool
                      ? 'bg-primary text-white'
                      : 'bg-white border border-gray-200 text-charcoal hover:border-primary'
                  }`}
                >
                  {tool === 'all' ? 'All guides' : tool}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Guides Grid */}
      <section className="py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredGuides.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {filteredGuides.map((guide) => (
                <Link key={guide.id} href={`/guides/${guide.slug}`}>
                  <div className="bg-white rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all p-6 h-full flex flex-col">
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className={`text-xs font-semibold px-2 py-1.5 rounded ${getDifficultyColor(guide.difficulty)}`}>
                          {guide.difficulty}
                        </span>
                        <span className="text-xs text-warm-gray">{guide.tool}</span>
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-charcoal hover:text-primary transition-colors mb-3 leading-snug flex-1">
                      {guide.title}
                    </h3>
                    <p className="text-warm-gray text-sm mb-4 leading-relaxed">
                      {guide.description}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-warm-gray">
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {guide.reads} reads
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-warm-gray text-lg">No guides found.</p>
              <button
                onClick={() => {
                  setSearchTerm('')
                  setSelectedTool('all')
                }}
                className="text-primary font-semibold hover:underline mt-2"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  )
}
